import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import type { RootState } from "../../../store/store";
import { updateUserByIdApi, UpdateUserRequest, UserType } from "../../auth/authApi";
import { getStoresPaginatedApi, StorePublic } from "../../store/storeApi";
import { maskCpfInput } from "../../dp/utils/format";
import type { AdminUserListItem } from "../api/adminUsersApi";
import { USER_TYPE_LABELS } from "./UserTypeBadge";
import ExtraRolesPicker, { sanitizeExtraRoles } from "./ExtraRolesPicker";
import RevokedModulesPicker from "./RevokedModulesPicker";
import { isFullAdminFromToken } from "../../../utils/authToken";
import type { AccessModule } from "../../../utils/accessModules";
import { formatPhoneInput, isPhoneValid, phoneFromApi, phoneToApi } from "../../../utils/phone";

import "../pages/AdminSistema.css";

type Props = {
  isOpen: boolean;
  /** Usuário já escolhido na tabela — este modal não busca ninguém. */
  user: AdminUserListItem | null;
  onClose: () => void;
  onUpdated?: () => void;
};

const STORES_LIMIT = 8;

const onlyDigits = (value: string) => value.replace(/\D/g, "");

/**
 * Tipos que têm lojas vinculadas.
 *
 * O backend aceita `storeIds` para os dois supervisores (`isSupervisorLike`),
 * mas o modal antigo só enviava quando o tipo era exatamente `SUPERVISOR` — a
 * seleção de lojas de um `SUPERVISOR_DE_CAMPO` era descartada em silêncio, e
 * não havia como alterá-la pela UI (BUG-008).
 */
const TIPOS_COM_LOJAS: UserType[] = ["SUPERVISOR", "SUPERVISOR_DE_CAMPO"];

const UserEditModal: React.FC<Props> = ({ isOpen, user, onClose, onUpdated }) => {
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [nomeCompleto, setNomeCompleto] = useState("");
  const [cpf, setCpf] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsappOptIn, setWhatsappOptIn] = useState(true);
  const [userType, setUserType] = useState<UserType>("SUPERVISOR");
  const [extraRoles, setExtraRoles] = useState<UserType[]>([]);
  const [revokedModules, setRevokedModules] = useState<AccessModule[]>([]);
  // Tipo, acessos e direitos: só o ADMIN pleno altera (ADR 0027). O
  // administrador restrito vê os campos travados e o payload não os leva.
  const canEditRights = isFullAdminFromToken(token);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  // Lojas
  const [stores, setStores] = useState<StorePublic[]>([]);
  const [storesPage, setStoresPage] = useState(1);
  const [storesTotal, setStoresTotal] = useState(0);
  const [loadingStores, setLoadingStores] = useState(false);
  const [storeSearchInput, setStoreSearchInput] = useState("");
  const [storeSearch, setStoreSearch] = useState("");
  const [selectedStoreIds, setSelectedStoreIds] = useState<Set<string>>(new Set());

  const mostraLojas = TIPOS_COM_LOJAS.includes(userType);

  const storesTotalPages = useMemo(
    () => Math.max(1, Math.ceil(storesTotal / STORES_LIMIT)),
    [storesTotal]
  );

  // Preenche o formulário sempre que o modal abre com outro usuário.
  useEffect(() => {
    if (!isOpen || !user) return;

    setNomeCompleto(user.nomeCompleto ?? "");
    setCpf(maskCpfInput(user.cpf ?? ""));
    setEmail(user.email ?? "");
    setPhone(phoneFromApi(user.phone));
    setWhatsappOptIn(user.whatsappOptIn ?? true);
    setUserType((user.userType as UserType) ?? "SUPERVISOR");
    setExtraRoles(user.extraRoles ?? []);
    setRevokedModules(user.revokedModules ?? []);
    setPassword("");
    setPasswordConfirm("");
    setSelectedStoreIds(new Set(user.storeIds ?? []));
    setStoresPage(1);
    setStoreSearchInput("");
    setStoreSearch("");
  }, [isOpen, user]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = anterior;
    };
  }, [isOpen, onClose, saving]);

  const fetchStores = useCallback(async () => {
    if (!token) return;
    setLoadingStores(true);
    try {
      const nome = storeSearch.trim();
      const data = await getStoresPaginatedApi(token, {
        page: storesPage,
        limit: STORES_LIMIT,
        ...(nome ? { nome } : {}),
      });
      setStores(data.items);
      setStoresTotal(data.total);
    } catch {
      toast.error("Erro ao buscar lojas.");
    } finally {
      setLoadingStores(false);
    }
  }, [token, storesPage, storeSearch]);

  // Só busca lojas quando o tipo escolhido as usa — evita uma requisição inútil
  // ao abrir a ficha de um ADMIN ou de um usuário do DP.
  useEffect(() => {
    if (!isOpen || !mostraLojas) return;
    fetchStores();
  }, [isOpen, mostraLojas, fetchStores]);

  const toggleStore = (storeId: string) => {
    setSelectedStoreIds((prev) => {
      const next = new Set(prev);
      if (next.has(storeId)) next.delete(storeId);
      else next.add(storeId);
      return next;
    });
  };

  const errosDoFormulario = useMemo(() => {
    const erros: string[] = [];

    if (!nomeCompleto.trim()) erros.push("Informe o nome completo.");
    if (onlyDigits(cpf).length !== 11) erros.push("CPF inválido (deve ter 11 dígitos).");
    if (!email.trim() || !email.includes("@")) erros.push("E-mail inválido.");
    if (!isPhoneValid(phone)) erros.push("Celular inválido: informe o DDD e o número.");

    // A senha é opcional na edição; se preencher, precisa conferir.
    if (password && !passwordConfirm) erros.push("Confirme a senha.");
    if (!password && passwordConfirm) erros.push("Preencha a senha antes de confirmar.");
    if (password && passwordConfirm && password !== passwordConfirm) {
      erros.push("Senha e confirmação não conferem.");
    }

    return erros;
  }, [nomeCompleto, cpf, email, phone, password, passwordConfirm]);

  const handleSave: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (!user) return;
    if (errosDoFormulario.length > 0) {
      toast.error(errosDoFormulario[0]);
      return;
    }

    const dto: UpdateUserRequest = {
      nomeCompleto: nomeCompleto.trim(),
      cpf: onlyDigits(cpf),
      email: email.trim(),
      phone: phoneToApi(phone),
      whatsappOptIn,
      ...(canEditRights
        ? {
            userType,
            extraRoles: sanitizeExtraRoles(userType, extraRoles),
            revokedModules: userType === "ADMIN_RESTRITO" ? revokedModules : [],
          }
        : {}),
      ...(mostraLojas ? { storeIds: Array.from(selectedStoreIds) } : {}),
      ...(password ? { password, passwordConfirm } : {}),
    };

    try {
      setSaving(true);
      await updateUserByIdApi(user.id, dto);
      toast.success("Usuário atualizado com sucesso.");
      onUpdated?.();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao atualizar usuário.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div
      className="admModalOverlay"
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div
        className="admModal"
        role="dialog"
        aria-modal="true"
        aria-label="Editar usuário"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSave}>
          <div className="admModal__header">
            <h2 className="admModal__title">Editar usuário</h2>
            <button
              type="button"
              className="admModal__close"
              onClick={onClose}
              disabled={saving}
              aria-label="Fechar"
            >
              ×
            </button>
          </div>

          <div className="admModal__body">
            <div className="admFormGroup">
              <label htmlFor="admEditNome">Nome completo</label>
              <input
                id="admEditNome"
                className="admInput"
                value={nomeCompleto}
                onChange={(e) => setNomeCompleto(e.target.value)}
                autoComplete="off"
              />
            </div>

            <div className="admFormGrid">
              <div className="admFormGroup">
                <label htmlFor="admEditCpf">CPF</label>
                <input
                  id="admEditCpf"
                  className="admInput"
                  value={cpf}
                  onChange={(e) => setCpf(maskCpfInput(e.target.value))}
                  inputMode="numeric"
                  autoComplete="off"
                />
              </div>

              <div className="admFormGroup">
                <label htmlFor="admEditEmail">E-mail</label>
                <input
                  id="admEditEmail"
                  className="admInput"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div className="admFormGroup">
                <label htmlFor="admEditPhone">Celular (WhatsApp)</label>
                <input
                  id="admEditPhone"
                  className="admInput"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                  inputMode="tel"
                  autoComplete="off"
                  placeholder="(21) 99999-8888"
                />
                <label className="admCheckInline">
                  <input
                    type="checkbox"
                    checked={whatsappOptIn && !!phone}
                    onChange={(e) => setWhatsappOptIn(e.target.checked)}
                    disabled={!phone}
                  />
                  Receber avisos no WhatsApp
                </label>
              </div>

              <div className="admFormGroup">
                <label htmlFor="admEditTipo">Tipo de usuário</label>
                <select
                  id="admEditTipo"
                  className="admSelect"
                  value={userType}
                  disabled={!canEditRights}
                  title={canEditRights ? undefined : "Só o Administrador altera o tipo do usuário."}
                  onChange={(e) => {
                    const next = e.target.value as UserType;
                    setUserType(next);
                    setExtraRoles((prev) => sanitizeExtraRoles(next, prev));
                  }}
                >
                  {(Object.keys(USER_TYPE_LABELS) as UserType[]).map((t) => (
                    <option key={t} value={t}>
                      {USER_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {userType === "ADMIN_RESTRITO" ? (
              <RevokedModulesPicker
                value={revokedModules}
                onChange={setRevokedModules}
                disabled={saving || !canEditRights}
              />
            ) : (
              <ExtraRolesPicker
                principal={userType}
                value={extraRoles}
                onChange={setExtraRoles}
                disabled={saving || !canEditRights}
              />
            )}

            <div className="admFormGrid">
              <div className="admFormGroup">
                <label htmlFor="admEditSenha">Nova senha (opcional)</label>
                <input
                  id="admEditSenha"
                  className="admInput"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <span className="admFormHint">
                  Deixe em branco para manter a senha atual.
                </span>
              </div>

              <div className="admFormGroup">
                <label htmlFor="admEditSenhaConfirm">Confirmar nova senha</label>
                <input
                  id="admEditSenhaConfirm"
                  className="admInput"
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
            </div>

            {mostraLojas ? (
              <div className="admFormGroup">
                <label>Lojas vinculadas</label>
                <div className="admStorePicker">
                  <div className="admStorePicker__search">
                    <input
                      className="admInput"
                      placeholder="Buscar loja..."
                      value={storeSearchInput}
                      onChange={(e) => setStoreSearchInput(e.target.value)}
                      onKeyDown={(e) => {
                        // O seletor vive dentro de um <form>: sem isto, Enter
                        // submeteria a edição inteira em vez de buscar.
                        if (e.key === "Enter") {
                          e.preventDefault();
                          setStoresPage(1);
                          setStoreSearch(storeSearchInput);
                        }
                      }}
                      aria-label="Buscar loja"
                    />
                    <button
                      type="button"
                      className="admBtn admBtn--secondary"
                      onClick={() => {
                        setStoresPage(1);
                        setStoreSearch(storeSearchInput);
                      }}
                    >
                      Buscar
                    </button>
                  </div>

                  <div className="admStorePicker__list">
                    {loadingStores ? (
                      <div className="admEmpty">Carregando lojas...</div>
                    ) : stores.length === 0 ? (
                      <div className="admEmpty">Nenhuma loja encontrada.</div>
                    ) : (
                      stores.map((store) => (
                        <label key={store.id} className="admStoreOption">
                          <input
                            type="checkbox"
                            checked={selectedStoreIds.has(store.id)}
                            onChange={() => toggleStore(store.id)}
                          />
                          <span className="admStoreOption__name">
                            {store.nomeFantasia}
                            {store.estado ? ` — ${store.estado}` : ""}
                          </span>
                        </label>
                      ))
                    )}
                  </div>

                  <div className="admStorePicker__footer">
                    <span>
                      {selectedStoreIds.size} loja(s) selecionada(s) · página {storesPage} de{" "}
                      {storesTotalPages}
                    </span>
                    <span style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        className="admBtn admBtn--secondary"
                        style={{ height: 30, padding: "0 10px" }}
                        disabled={storesPage <= 1 || loadingStores}
                        onClick={() => setStoresPage((p) => p - 1)}
                      >
                        Anterior
                      </button>
                      <button
                        type="button"
                        className="admBtn admBtn--secondary"
                        style={{ height: 30, padding: "0 10px" }}
                        disabled={storesPage >= storesTotalPages || loadingStores}
                        onClick={() => setStoresPage((p) => p + 1)}
                      >
                        Próxima
                      </button>
                    </span>
                  </div>
                </div>
                <span className="admFormHint">
                  A seleção vale para as lojas de todas as páginas, não só da atual.
                </span>
              </div>
            ) : (
              <p className="admMuted" style={{ margin: 0 }}>
                Usuários do tipo {USER_TYPE_LABELS[userType]} não têm lojas vinculadas.
              </p>
            )}

            {errosDoFormulario.length > 0 ? (
              <ul className="admErrorList">
                {errosDoFormulario.map((erro) => (
                  <li key={erro}>{erro}</li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="admModal__footer">
            <button
              type="button"
              className="admBtn admBtn--secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="admBtn admBtn--primary"
              disabled={saving || errosDoFormulario.length > 0}
            >
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserEditModal;
